import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getPermissionsFromToken, getRoleFromToken } from '../utils/jwt';

/**
 * Hook for checking user permissions
 * Uses JWT token to determine permissions instead of localStorage
 */
export const usePermissions = () => {
  const { token } = useAuth();

  const permissions = useMemo(() => {
    if (!token) return [];
    return getPermissionsFromToken(token);
  }, [token]);

  const role = useMemo(() => {
    if (!token) return null;
    return getRoleFromToken(token);
  }, [token]);

  const hasPermission = (permission) => {
    if (!permissions || permissions.length === 0) return false;
    return permissions.includes(permission);
  };

  const hasRole = (requiredRole) => {
    if (!role) return false;
    return role === requiredRole;
  };

  const hasAnyPermission = (permissionList) => {
    if (!permissions || permissions.length === 0) return false;
    return permissionList.some(permission => permissions.includes(permission));
  };

  const hasAllPermissions = (permissionList) => {
    if (!permissions || permissions.length === 0) return false;
    return permissionList.every(permission => permissions.includes(permission));
  };

  const canView = (resource) => {
    return hasPermission(`view_${resource}`);
  };

  const canCreate = (resource) => {
    return hasPermission(`create_${resource}`) || hasPermission('create_listings');
  };

  const canUpdate = (resource) => {
    return hasPermission(`update_${resource}`) || hasPermission('manage_own_listings');
  };

  const canDelete = (resource) => {
    return hasPermission(`delete_${resource}`) || hasPermission('manage_own_listings');
  };

  const canManage = (resource) => {
    return hasPermission(`manage_${resource}`);
  };

  const isAdmin = () => {
    return hasRole('Admin') || hasPermission('system_admin');
  };

  const isManager = () => {
    return hasRole('Manager') || hasPermission('manage_platform');
  };

  const isSeller = () => {
    return hasRole('Seller') || hasPermission('create_listings');
  };

  const isParticipant = () => {
    return hasRole('Participant') || hasPermission('place_bids');
  };

  const getRoleInfo = () => {
    const roleInfo = {
      'Observer': { color: 'text-gray-400', badge: 'bg-gray-600' },
      'Participant': { color: 'text-blue-400', badge: 'bg-blue-600' },
      'Seller': { color: 'text-green-400', badge: 'bg-green-600' },
      'Manager': { color: 'text-orange-400', badge: 'bg-orange-600' },
      'Admin': { color: 'text-red-400', badge: 'bg-red-600' }
    };
    return roleInfo[role] || { color: 'text-gray-400', badge: 'bg-gray-600' };
  };

  const getWelcomeMessage = () => {
    const messages = {
      'Observer': 'Explore our marketplace and discover amazing products and auctions!',
      'Participant': 'Welcome! You can browse, bid, and purchase items on our platform.',
      'Seller': 'Ready to sell? Create listings and track your sales performance.',
      'Manager': 'Manage platform operations and oversee user activities.',
      'Admin': 'Full system access - configure and manage the entire platform.'
    };
    return messages[role] || 'Welcome to the platform!';
  };

  return {
    permissions,
    role,
    hasPermission,
    hasRole,
    hasAnyPermission,
    hasAllPermissions,
    canView,
    canCreate,
    canUpdate,
    canDelete,
    canManage,
    isAdmin,
    isManager,
    isSeller,
    isParticipant,
    getRoleInfo,
    getWelcomeMessage
  };
};
